import React, { useState, useEffect } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/CommonExport';
import { TextField, Grid, Autocomplete, CircularProgress, Box, Typography, IconButton } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { Button } from '@mui/material';

function TopicsDashboard() {
    const navigate = useNavigate();
    const [topicsData, setTopicsData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [matchFilter, setMatchFilter] = useState(null);
    const [cidFilter, setCidFilter] = useState(null);
    const [matchData, setMatchData] = useState([]);
    const [competitionData, setCompetitionData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        // Fetch competitions data
        axios.get(`${BASE_URL}/admin/competitions/get_competitions?start_date=${startDate}&end_date=${endDate}`)
            .then((result) => {
                setCompetitionData(result.data.data.competitions);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [startDate, endDate]);

    useEffect(() => {
        const fetchMatches = async () => {
            const requestBody = {};
            if (startDate) requestBody.startDate = startDate;
            if (endDate) requestBody.endDate = endDate;
            if (cidFilter) requestBody.cid = cidFilter;

            try {
                const response = await axios.post(`${BASE_URL}/tracking/matches`, requestBody);
                setMatchData(response.data.data.users);
            } catch (error) {
                console.error('Error fetching matches:', error);
            }
        };

        fetchMatches();
    }, [startDate, endDate, cidFilter]);

    useEffect(() => {
        const fetchTopicsData = async () => {
            setLoading(true);
            const requestBody = {};
            
            if (startDate) requestBody.start_date = startDate;
            if (endDate) requestBody.end_date = endDate;
            if (matchFilter) requestBody.match_id = matchFilter.match_id;
            if (cidFilter) requestBody.cid = cidFilter;

            try {
                const params = new URLSearchParams();   
                if (startDate) params.append('start_date', startDate);
                if (endDate) params.append('end_date', endDate);
                if (matchFilter) params.append('mid', matchFilter.match_id);
                if (cidFilter) params.append('cid', cidFilter);
                
                const response = await axios.get(`${BASE_URL}/admin/topics/dashboard/analytics?${params.toString()}`);
                const formattedData = response.data.data.map(topic => ({
                    ...topic,
                    created_at: new Date(topic.created_at).toLocaleString()
                }));
                setTopicsData(formattedData);
                setFilteredData(formattedData);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching topics data:', error);
                setLoading(false);
            }
        };

        fetchTopicsData();
    }, [startDate, endDate, matchFilter, cidFilter]);

    const calculateTotals = (data) => {
        return {
            totalTopics: data.length
        };
    };

    const totals = calculateTotals(filteredData);

    const exportToCSV = () => {
        const csvData = filteredData.map(item => ({
            Title: item.title,
            Match_Title: item.match_title,
            Player_Name: item.player_name,
            Post_Count: item.post_count,
            Clicks: item.click_count,
            Created_At: item.created_at,
            Tags: item.tags.join(', ')
        }));
        const csv = Papa.unparse(csvData);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'topics_analytics.csv';
        a.click();
    }

    const columns = [
        { field: 'title', headerName: 'Title', flex: 1 },
        { field: 'match_title', headerName: 'Match Title', flex: 1 },
        { field: 'player_name', headerName: 'Player Name', flex: 1 },
        { field: 'post_count', headerName: 'Post Count', flex: 1, type: 'number', align: 'left', headerAlign: 'left' },
        {field: 'click_count', headerName: 'Clicks', flex: 1, type: 'number', align: 'left', headerAlign: 'left'},
        { 
            field: 'created_at', 
            headerName: 'Created At', 
            flex: 1,
            valueFormatter: (params) => {
                return new Date(params.value).toLocaleString();
            },
            sortComparator: (v1, v2) => {
                return new Date(v1).getTime() - new Date(v2).getTime();
            }
        },
        {
            field: 'tags', 
            headerName: 'Tags', 
            flex: 1,
            renderCell: (params) => {
                if (!params.value || !Array.isArray(params.value)) {
                    return '-';
                }
                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {params.value.map((tag, index) => (
                            <span
                                key={index}
                                style={{
                                    backgroundColor: '#e3f2fd',
                                    color: '#1976d2',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.75rem',
                                    fontWeight: '500'
                                }}
                            >
                                {tag}
                            </span>
                        ))}
                    </div>
                );
            }
        }
    ];

    return (
        <Component>
            <div className="page-body">
                <div className="container-fluid">
                    <div className="page-title">
                        <div className="row">
                            <div className="col-6 d-flex align-items-center">
                                <IconButton 
                                    onClick={() => navigate(-1)}
                                    sx={{ mr: 2 }}
                                >
                                    <ArrowBackIcon />
                                </IconButton>
                                <h3>Topics Analytics</h3>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="container-fluid">
                    <div className="row">
                        <div className="col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <Grid container spacing={2} sx={{ mb: 2 }}>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="From Date"
                                                value={startDate}
                                                onChange={(e) => setStartDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <TextField
                                                fullWidth
                                                type="date"
                                                label="To Date"
                                                value={endDate}
                                                onChange={(e) => setEndDate(e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={competitionData}
                                                getOptionLabel={(option) => option.title || ''}
                                                isOptionEqualToValue={(option, value) => option.cid === value.cid}
                                                value={competitionData.find(comp => comp.cid === cidFilter) || null}
                                                onChange={(event, newValue) => {
                                                    setCidFilter(newValue ? newValue.cid : null);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Competition" />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={2}>
                                            <Autocomplete
                                                options={matchData}
                                                getOptionLabel={(option) => option.title + ' - ' + option.subtitle + ' - ' + option.match_id || ''}
                                                isOptionEqualToValue={(option, value) => option.match_id === value.match_id}
                                                value={matchFilter}
                                                onChange={(event, newValue) => {
                                                    setMatchFilter(newValue);
                                                }}
                                                renderInput={(params) => (
                                                    <TextField {...params} label="Match" />
                                                )}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Button variant="contained" color="primary" onClick={exportToCSV}>Export to CSV</Button>
                                    <Box sx={{ mb: 2, p: 2, backgroundColor: '#e6f3ff', borderRadius: 1 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Summary
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6} md={2}>
                                                <Typography variant="subtitle2">Total Topics</Typography>
                                                <Typography variant="h6">{totals.totalTopics}</Typography>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {loading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : (
                                        <div style={{ height: 600, width: '100%' }}>
                                            <DataGrid
                                                rows={filteredData}
                                                columns={columns}
                                                pageSize={10}
                                                rowsPerPageOptions={[10, 25, 50]}
                                                checkboxSelection={false}
                                                disableSelectionOnClick
                                                getRowId={(row) => row.id}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Component>
    );
}

export default TopicsDashboard;
